import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Mail, Calendar, User, MapPin, CheckCircle, AlertCircle, XCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReportsHistory = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, ready, needs-attention, not-ready
  const [emailModal, setEmailModal] = useState({ open: false, report: null });
  const [emailData, setEmailData] = useState({ to_email: '', message: '' });

  const password = sessionStorage.getItem('admin_password');

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const auth = btoa('admin:' + password);
      const response = await fetch(`${API_URL}/api/admin/readiness-reports`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data);
      } else if (response.status === 401) {
        navigate('/admin/login');
      }
    } catch (error) {
      logger.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (reportId, filename) => {
    try {
      const auth = btoa('admin:' + password);
      const response = await fetch(`${API_URL}/api/admin/readiness-reports/${reportId}`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Report downloaded');
      } else {
        toast.error('Failed to download report');
      }
    } catch (error) {
      logger.error('Error downloading report:', error);
      toast.error('Error downloading report');
    }
  };

  const openEmailModal = (report) => {
    setEmailModal({ open: true, report });
    // Pre-fill client email if available
    if (report.report_data?.client_email) {
      setEmailData({ 
        to_email: report.report_data.client_email,
        message: 'Please find attached your TravelSafe Readiness Report. Review the assessment and let us know if you have any questions.'
      });
    }
  };

  const sendEmail = async () => {
    if (!emailData.to_email) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      const auth = btoa('admin:' + password);
      const response = await fetch(`${API_URL}/api/admin/readiness-reports/${emailModal.report.id}/email`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${auth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailData)
      });

      if (response.ok) {
        toast.success(`Report emailed to ${emailData.to_email}`);
        setEmailModal({ open: false, report: null });
        setEmailData({ to_email: '', message: '' });
        fetchReports(); // Refresh to show updated email history
      } else {
        toast.error('Failed to send email');
      }
    } catch (error) {
      logger.error('Error sending email:', error);
      toast.error('Error sending email');
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'Ready') {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    } else if (status && status.includes('Attention')) {
      return <AlertCircle className="w-5 h-5 text-orange-600" />;
    } else {
      return <XCircle className="w-5 h-5 text-red-600" />;
    }
  };

  const getStatusColor = (status) => {
    if (status === 'Ready') return 'bg-green-100 text-green-800 border-green-200';
    if (status && status.includes('Attention')) return 'bg-orange-100 text-orange-800 border-orange-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const filteredReports = reports.filter(report => {
    if (filter === 'all') return true;
    if (filter === 'ready') return report.readiness_status === 'Ready';
    if (filter === 'needs-attention') return report.readiness_status?.includes('Attention');
    if (filter === 'not-ready') return report.readiness_status === 'Not Ready';
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Reports History</h1>
                  <p className="text-sm text-gray-600">View and manage all generated readiness reports</p>
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-blue-600">{filteredReports.length}</p>
              <p className="text-sm text-gray-600">Total Reports</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Reports ({reports.length})
            </button>
            <button
              onClick={() => setFilter('ready')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'ready' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Ready ({reports.filter(r => r.readiness_status === 'Ready').length})
            </button>
            <button
              onClick={() => setFilter('needs-attention')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'needs-attention' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Needs Attention ({reports.filter(r => r.readiness_status?.includes('Attention')).length})
            </button>
            <button
              onClick={() => setFilter('not-ready')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'not-ready' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Not Ready ({reports.filter(r => r.readiness_status === 'Not Ready').length})
            </button>
          </div>
        </div>

        {/* Reports List */}
        <div className="space-y-4">
          {filteredReports.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No reports found</p>
              <Button
                onClick={() => navigate('/admin/readiness-reports')}
                className="mt-4"
              >
                Generate New Report
              </Button>
            </div>
          ) : (
            filteredReports.map((report) => (
              <div key={report.id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-xl font-bold text-gray-900">{report.pet_name}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border flex items-center gap-1 ${getStatusColor(report.readiness_status)}`}>
                        {getStatusIcon(report.readiness_status)}
                        {report.readiness_status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-gray-600">
                        <User className="w-4 h-4" />
                        <span className="text-sm">{report.owner_name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">{report.destination_country || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">
                          {new Date(report.generated_at).toLocaleDateString()} at {new Date(report.generated_at).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>

                    {/* Email History */}
                    {report.email_history && report.email_history.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                        <p className="text-sm font-medium text-blue-900 mb-2">📧 Email History:</p>
                        <div className="space-y-1">
                          {report.email_history.slice(0, 2).map((email, i) => (
                            <p key={i} className="text-xs text-blue-700">
                              Sent to {email.sent_to} on {new Date(email.sent_at).toLocaleDateString()}
                            </p>
                          ))}
                          {report.email_history.length > 2 && (
                            <p className="text-xs text-blue-600">+{report.email_history.length - 2} more</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      onClick={() => downloadReport(report.id, report.filename)}
                      className="flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </Button>
                    <Button
                      onClick={() => openEmailModal(report)}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Mail className="w-4 h-4" />
                      Email
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Email Modal */}
      {emailModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Email Report: {emailModal.report?.pet_name}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Email *
                </label>
                <input
                  type="email"
                  value={emailData.to_email}
                  onChange={(e) => setEmailData({ ...emailData, to_email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="client@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Message (optional)
                </label>
                <textarea
                  value={emailData.message}
                  onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add a personal message..."
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setEmailModal({ open: false, report: null });
                    setEmailData({ to_email: '', message: '' });
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendEmail}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Send Email
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsHistory;
